import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';

import type {
	Collaborators,
	CrossJoinTargetProduct,
	CrossJoinIntegration,
} from '@atlassian/cross-join-nudges/types';
import { buildNonContextualNote } from '@atlassian/cross-join-nudges/services/non-contextual-notes';
import { RequestAccessErrorFlag } from '@atlassian/cross-join-nudges/request-access-error-flag';
import { RequestAccessPromptFlag } from '@atlassian/cross-join-nudges/request-access-prompt-flag';
import { RequestAccessSuccessFlag } from '@atlassian/cross-join-nudges/request-access-success-flag';
import { requestAccess } from '@atlassian/cross-join-nudges/services/request-access';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import {
	getFeatureVisitedCount,
	setUserOptedOut,
	snoozeFeatureFor30Days,
} from '../services/feature-visited.ts';
import {
	fireFlagDismissed,
	fireFlagShown,
	fireFlagSnoozed,
	fireJoinButtonClicked,
	fireUserOptedOut,
	getExperimentAnalytics,
} from '../services/analytics.ts';
import type { CrossJoinNudgesInput } from '../services/run-cross-join-nudges.ts';

type ConfluenceCrossJoinIntegration = Extract<
	CrossJoinIntegration,
	'crossJoinNudgesV3' | 'crossJoinNudgesConfluenceToJpd'
>;

type ShowNudgeFlagInput = Pick<
	CrossJoinNudgesInput,
	'cloudId' | 'flags' | 'createAnalyticsEvent' | 'displayName' | 'formatMessage'
> & {
	product: CrossJoinTargetProduct;
	integration: ConfluenceCrossJoinIntegration;
	userId: string;
	collaborators: Collaborators;
	pushRoute: CrossJoinNudgesInput['push'];
	onDisplayed?: () => void;
};

const crossJoinRoutes: Record<ConfluenceCrossJoinIntegration, string> = {
	crossJoinNudgesV3: '/jira',
	crossJoinNudgesConfluenceToJpd: '/jira/projects',
};

export const showNudgeFlag = async ({
	product,
	integration,
	userId,
	displayName,
	cloudId,
	collaborators,
	createAnalyticsEvent,
	flags,
	pushRoute,
	formatMessage,
	onDisplayed,
}: ShowNudgeFlagInput): Promise<void> => {
	const featureVisitedCount = getFeatureVisitedCount(integration);
	const attributes = {
		collaborators: collaborators.length,
		userId,
		featureVisitedCount,
	};
	const analytics = getExperimentAnalytics(integration, createAnalyticsEvent, {
		attributes,
	});

	const dismiss = async (id: string | number, flags: FlagsStateContainer) => {
		fireFlagDismissed(analytics);
		await flags.hideFlag(id);
	};

	const dismissAndSnooze = async (id: string | number, flags: FlagsStateContainer) => {
		await dismiss(id, flags);
		fireFlagSnoozed(analytics);
		snoozeFeatureFor30Days(integration);
	};

	const dismissAndOptout = async (id: string | number, flags: FlagsStateContainer) => {
		await dismiss(id, flags);
		fireUserOptedOut(analytics);
		setUserOptedOut(integration);
	};

	// Analytics parity (PUMA-1934): when the choreographer fix is active, `onDisplayed`
	// is supplied and we defer the "flag shown" analytics until the flag is actually
	// displayed (i.e. not blocked by the orchestrator). When it is absent (fix off),
	// retain the previous eager behaviour below.
	const handleDisplayed = onDisplayed
		? () => {
				fireFlagShown(analytics);
				onDisplayed();
			}
		: undefined;

	await flags.showFlag({
		customComponent: (props) => (
			<ChoreographerRequestAccessPromptFlag
				id={props.id}
				integration={integration}
				collaborators={collaborators}
				targetProduct={product}
				viewCount={featureVisitedCount}
				onDisplayed={handleDisplayed}
				onDismiss={() => dismiss(props.id, flags)}
				onSnoozeAndDismiss={() => dismissAndSnooze(props.id, flags)}
				onOptoutAndDismiss={() => dismissAndOptout(props.id, flags)}
				onSubmit={async () => {
					try {
						const nonContextualNote = fg('twcg-1402-non-contextual-ra-note-gate')
							? buildNonContextualNote({
									displayName: displayName ?? null,
									targetProduct: product,
									formatMessage,
								})
							: undefined;
						const accessStatus = await requestAccess(
							product,
							'confluence',
							integration,
							userId,
							cloudId,
							createAnalyticsEvent,
							nonContextualNote,
						);
						if (
							accessStatus === 'ACCESS_GRANTED' ||
							accessStatus === 'ACCESS_EXISTS' ||
							accessStatus === 'APPROVED_REQUEST_EXISTS'
						) {
							pushRoute(crossJoinRoutes[integration]);
						} else if (
							accessStatus === 'PENDING_REQUEST_CREATED' ||
							accessStatus === 'PENDING_REQUEST_EXISTS'
						) {
							await flags.hideFlag(props.id);
							await showSuccessFlag(flags);
						} else {
							await flags.hideFlag(props.id);
							await showErrorFlag(flags, product);
						}
					} catch {
						await flags.hideFlag(props.id);
						await showErrorFlag(flags, product);
					}
					fireJoinButtonClicked(analytics);
				}}
			/>
		),
	});

	// When the fix is off (no onDisplayed), fire eagerly as before. When on, this is
	// fired from handleDisplayed once the flag is actually displayed.
	if (!handleDisplayed) {
		fireFlagShown(analytics);
	}
};
export const showSuccessFlag = async (flags: FlagsStateContainer): Promise<void> => {
	await flags.showFlag({
		customComponent: (props) => (
			<RequestAccessSuccessFlag id={props.id} onDismiss={() => flags.hideFlag(props.id)} />
		),
	});
};

export const showErrorFlag = async (
	flags: FlagsStateContainer,
	product: CrossJoinTargetProduct,
): Promise<void> => {
	await flags.showFlag({
		customComponent: (props) => (
			<RequestAccessErrorFlag
				id={props.id}
				targetProduct={product}
				onDismiss={() => flags.hideFlag(props.id)}
			/>
		),
	});
};

// Choreographer Wrapper around RequestAccessPromptFlag
export function ChoreographerRequestAccessPromptFlag({
	onDisplayed,
	...passThrough
}: ComponentProps<typeof RequestAccessPromptFlag> & {
	onDisplayed?: () => void;
}): React.JSX.Element | null {
	const { useChoreographedRender } = useChoreographerAssets();

	const canShow = useChoreographedRender({
		messageId: 'cc-cross-join-nudge',
	});

	useEffect(() => {
		if (canShow) {
			onDisplayed?.();
		}
	}, [canShow, onDisplayed]);

	return canShow ? <RequestAccessPromptFlag {...passThrough} /> : null;
}
